import React from "react";
import { Timeline, Tag, Typography, Tooltip } from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  ITripStatusHistory,
  TripStatus,
  getTripStatusLabel,
  getTripStatusColor,
} from "../../../models/trip/response";
import styles from "./style.module.scss";

const { Text } = Typography;

interface IProps {
  statusHistory?: ITripStatusHistory[];
}

const TripStatusTimeline: React.FC<IProps> = ({ statusHistory }) => {
  const { t } = useTranslation();

  if (!statusHistory || statusHistory.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text type="secondary">{t("no_status_history")}</Text>
      </div>
    );
  }

  const getStatusIcon = (status: TripStatus) => {
    switch (status) {
      case TripStatus.PENDING:
        return <PlusCircleOutlined />;
      case TripStatus.ACTIVE:
        return <PlayCircleOutlined />;
      case TripStatus.COMPLETED:
        return <CheckCircleOutlined />;
      case TripStatus.CANCELLED:
        return <CloseCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusAntColor = (status: TripStatus): string => {
    switch (status) {
      case TripStatus.PENDING:
        return "blue";
      case TripStatus.ACTIVE:
        return "green";
      case TripStatus.COMPLETED:
        return "blue";
      case TripStatus.CANCELLED:
        return "red";
      default:
        return "gray";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getChangedByName = (changedBy: ITripStatusHistory["changedBy"]) => {
    if (!changedBy) return t("system");
    if (changedBy.name) return changedBy.name;
    if (changedBy.firstName || changedBy.lastName) {
      return `${changedBy.firstName || ""} ${changedBy.lastName || ""}`.trim();
    }
    return t("user") + ` #${changedBy.id}`;
  };

  const renderMetadata = (
    metadata: ITripStatusHistory["metadata"],
    toStatus: TripStatus
  ) => {
    if (!metadata) return null;

    const items: React.ReactNode[] = [];

    // For trip creation (PENDING)
    if (toStatus === TripStatus.PENDING) {
      if (metadata.orders_count) {
        items.push(
          <span key="orders">
            {t("orders_count")}: {metadata.orders_count}
          </span>
        );
      }
    }

    // For activation (ACTIVE)
    if (toStatus === TripStatus.ACTIVE) {
      if (metadata.total_distance) {
        items.push(
          <span key="distance">
            {t("total_distance")}: {Number(metadata.total_distance).toFixed(2)}{" "}
            {t("km")}
          </span>
        );
      }
      if (metadata.duration_minutes) {
        items.push(
          <span key="duration">
            {t("estimated_duration")}: {metadata.duration_minutes}{" "}
            {t("minute_short")}
          </span>
        );
      }
    }

    // For completion (COMPLETED)
    if (toStatus === TripStatus.COMPLETED) {
      if (metadata.completed_orders !== undefined) {
        items.push(
          <span key="completed">
            {t("completed_orders")}: {metadata.completed_orders}
          </span>
        );
      }
      if (metadata.failed_orders !== undefined && metadata.failed_orders > 0) {
        items.push(
          <span key="failed" style={{ color: "#ff4d4f" }}>
            {t("failed_orders")}: {metadata.failed_orders}
          </span>
        );
      }
      if (metadata.actual_duration_minutes) {
        items.push(
          <span key="actual">
            {t("actual_duration")}: {metadata.actual_duration_minutes}{" "}
            {t("minute_short")}
          </span>
        );
      }
    }

    // For cancellation (CANCELLED)
    if (toStatus === TripStatus.CANCELLED) {
      if (metadata.cancellation_type) {
        items.push(
          <span key="type">
            {t("cancellation_type")}: {t(metadata.cancellation_type)}
          </span>
        );
      }
    }

    if (items.length === 0) return null;

    return <div className={styles.metadata}>{items}</div>;
  };

  // Sort by createdAt ascending (oldest first)
  const sortedHistory = [...statusHistory].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <div className={styles.container}>
      <Timeline
        items={sortedHistory.map((entry, index) => ({
          color: getStatusAntColor(entry.toStatus),
          dot: getStatusIcon(entry.toStatus),
          children: (
            <div className={styles.timelineItem}>
              <div className={styles.header}>
                <Tag color={getTripStatusColor(entry.toStatus)}>
                  {t(getTripStatusLabel(entry.toStatus))}
                </Tag>
                <Text type="secondary" className={styles.timestamp}>
                  {formatDate(entry.createdAt)}
                </Text>
              </div>

              {entry.fromStatus !== null && (
                <div className={styles.transition}>
                  <Text type="secondary">
                    {t("from")}{" "}
                    <Tag color={getTripStatusColor(entry.fromStatus)} style={{ margin: 0 }}>
                      {t(getTripStatusLabel(entry.fromStatus))}
                    </Tag>{" "}
                    {t("to")}{" "}
                    <Tag color={getTripStatusColor(entry.toStatus)} style={{ margin: 0 }}>
                      {t(getTripStatusLabel(entry.toStatus))}
                    </Tag>
                  </Text>
                </div>
              )}

              <div className={styles.changedBy}>
                <UserOutlined style={{ marginInlineEnd: 4 }} />
                <Text type="secondary">{getChangedByName(entry.changedBy)}</Text>
              </div>

              {entry.changeReason && (
                <div className={styles.reason}>
                  <Text type="warning">
                    {t("reason")}: {entry.changeReason}
                  </Text>
                </div>
              )}

              {renderMetadata(entry.metadata, entry.toStatus)}
            </div>
          ),
        }))}
      />
    </div>
  );
};

export default TripStatusTimeline;
